/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * FormSubmissionsPage — view, search, and export submissions for a form section.
 * Route: /dashboard/applications/:id/page-builder/:pageId/forms/:formId/submissions
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../theme/ThemeContext';
import { useFormSubmissions, deleteSubmission, getExportUrl } from '../hooks/usePages';
import { useQueryClient } from '@tanstack/react-query';

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch (_) {
    return iso;
  }
}

export default function FormSubmissionsPage() {
  const { id: appId, pageId, formId } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('submitted');
  const [deleting, setDeleting] = useState(null);

  const { data, isLoading, isError, error, refetch } = useFormSubmissions(formId, {
    page,
    pageSize: 25,
    search,
    dateFrom,
    dateTo,
    status: statusFilter,
  });

  const submissions  = data?.results || [];
  const totalCount   = data?.count || data?.total || 0;
  const totalPages   = Math.ceil(totalCount / 25) || 1;

  // Use fields_schema from API to build ordered, labelled columns.
  // Includes active, archived (is_archived), and orphaned (is_orphaned) fields.
  // Falls back to raw keys from the first submission if schema is missing.
  const fieldsSchema = data?.fields_schema || [];
  const columns = fieldsSchema.length > 0
    ? fieldsSchema.map(f => ({
        id: f.id,
        label: f.label || f.id,
        isArchived: f.is_archived || false,
        isOrphaned: f.is_orphaned || false,
      }))
    : submissions.length > 0
      ? Object.keys(submissions[0].data || {}).map(k => ({ id: k, label: k, isArchived: false, isOrphaned: false }))
      : [];

  // For multi-stage forms the API returns stages_schema: [{stageLabel, field_ids}].
  // Build stage groups by mapping each stage's field_ids to the actual column objects.
  // Any columns not assigned to a stage (orphaned / archived after config change) go
  // into a trailing "Other" group.
  const stagesSchema = data?.stages_schema || [];
  const stageGroups = (() => {
    if (stagesSchema.length === 0) return [];
    const colById = Object.fromEntries(columns.map(c => [c.id, c]));
    const assignedIds = new Set(stagesSchema.flatMap(s => s.field_ids));
    const groups = stagesSchema
      .map(s => ({
        stageLabel: s.stageLabel,
        columns: s.field_ids.map(id => colById[id]).filter(Boolean),
      }))
      .filter(g => g.columns.length > 0);
    const ungrouped = columns.filter(c => !assignedIds.has(c.id));
    if (ungrouped.length > 0) groups.push({ stageLabel: 'Other', columns: ungrouped });
    return groups;
  })();
  // When stage groups exist, use their ordering for both header and body cells.
  const orderedColumns = stageGroups.length > 0
    ? stageGroups.flatMap(g => g.columns)
    : columns;

  // Per-stage visual styling helpers (only used when stageGroups.length > 0).
  // All stages share the same lavender tint — the purple border is the separator.
  const STAGE_TINT   = '#faf5ff';
  const stageFirstColIds = new Set(stageGroups.map(g => g.columns[0]?.id).filter(Boolean));
  const stageTint   = () => STAGE_TINT;
  const stageBorder = (colId) => stageFirstColIds.has(colId) ? '2px solid #c4b5fd' : undefined;

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const handleDelete = async (subId) => {
    if (!window.confirm('Delete this submission? This cannot be undone.')) return;
    setDeleting(subId);
    try {
      await deleteSubmission(formId, subId);
      queryClient.invalidateQueries({ queryKey: ['formSubmissions', formId] });
    } catch (err) {
      alert(err.message || 'Failed to delete submission.');
    } finally {
      setDeleting(null);
    }
  };

  const exportUrl = getExportUrl(formId);

  return (
    <div
      className="d-flex flex-column w-100"
      style={{ background: '#f8fafc', minHeight: '100vh' }}
    >
      {/* Header */}
      <div className="w-100 px-3 py-3 px-md-4 bg-white border-bottom d-flex flex-wrap align-items-center justify-content-between gap-3 shadow-sm">
        <div className="d-flex align-items-center gap-3">
          <button
            className="btn btn-light rounded-circle shadow-sm"
            onClick={() => navigate(`/dashboard/applications/${appId}/page-builder/${pageId}/edit`)}
            style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <i className="bi bi-arrow-left" />
          </button>
          <div>
            <h4 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
              <i className="bi bi-inbox" style={{ color: theme.primary }} />
              {data?.form_title ? `${data.form_title} — Submissions` : 'Form Submissions'}
            </h4>
            <small className="text-muted">
              {totalCount} total submission{totalCount !== 1 ? 's' : ''}
            </small>
          </div>
        </div>

        {/* Export CSV */}
        <a
          href={exportUrl}
          className="btn btn-sm btn-outline-secondary"
          target="_blank"
          rel="noopener noreferrer"
        >
          <i className="bi bi-download me-1" />
          Export CSV
        </a>
      </div>

      {/* Status tabs */}
      <div className="container-fluid pt-3 px-3 px-md-4" style={{ maxWidth: 1200 }}>
        <div className="d-flex gap-2 mb-3">
          {[
            { key: 'submitted', label: 'Submitted' },
            { key: 'draft',     label: 'Drafts'    },
            { key: 'all',       label: 'All'        },
          ].map(tab => (
            <button
              key={tab.key}
              type="button"
              className="btn btn-sm"
              style={{
                background: statusFilter === tab.key ? theme.primary : '#f1f5f9',
                color: statusFilter === tab.key ? '#fff' : '#374151',
                border: 'none',
                fontWeight: statusFilter === tab.key ? 600 : 400,
                borderRadius: 6,
                padding: '4px 14px',
                fontSize: 12,
              }}
              onClick={() => { setStatusFilter(tab.key); setPage(1); }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="container-fluid px-3 pb-3 px-md-4" style={{ maxWidth: 1200 }}>
        <form
          className="bg-white rounded border shadow-sm p-3 mb-4 d-flex flex-wrap align-items-end gap-3"
          onSubmit={handleSearch}
        >
          <div style={{ flex: '1 1 200px' }}>
            <label className="form-label small fw-semibold mb-1">Search</label>
            <input
              className="form-control form-control-sm"
              placeholder="Search submissions…"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
            />
          </div>
          <div style={{ flex: '0 0 160px' }}>
            <label className="form-label small fw-semibold mb-1">From</label>
            <input
              className="form-control form-control-sm"
              type="date"
              value={dateFrom}
              onChange={e => { setDateFrom(e.target.value); setPage(1); }}
            />
          </div>
          <div style={{ flex: '0 0 160px' }}>
            <label className="form-label small fw-semibold mb-1">To</label>
            <input
              className="form-control form-control-sm"
              type="date"
              value={dateTo}
              onChange={e => { setDateTo(e.target.value); setPage(1); }}
            />
          </div>
          <button
            type="submit"
            className="btn btn-sm"
            style={{ background: theme.primary, color: '#fff', border: 'none' }}
          >
            <i className="bi bi-search me-1" />
            Search
          </button>
          {(search || dateFrom || dateTo) && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => { setSearch(''); setSearchInput(''); setDateFrom(''); setDateTo(''); setPage(1); }}
            >
              Clear
            </button>
          )}
        </form>

        {/* Table */}
        {isLoading && (
          <div className="text-center py-5">
            <span className="spinner-border" style={{ color: theme.primary }} />
          </div>
        )}

        {isError && (
          <div className="alert alert-danger">
            {error?.message || 'Failed to load submissions.'}{' '}
            <button className="btn btn-sm btn-outline-danger ms-2" onClick={refetch}>Retry</button>
          </div>
        )}

        {!isLoading && !isError && submissions.length === 0 && (
          <div className="bg-white rounded border shadow-sm p-5 text-center text-muted">
            <i className="bi bi-inbox" style={{ fontSize: 48, opacity: 0.2 }} />
            <div className="fw-semibold mt-3">No submissions yet</div>
            <div className="small mt-1">
              {search ? 'No results match your search.' : 'Submissions from the public form will appear here.'}
            </div>
          </div>
        )}

        {!isLoading && !isError && submissions.length > 0 && (
          <>
            <div className="bg-white rounded border shadow-sm overflow-auto">
              <table className="table table-hover mb-0" style={{ minWidth: 600 }}>
                <thead>
                  {/* ── Stage header row — only for multi-stage forms ── */}
                  {stageGroups.length > 0 && (
                    <tr style={{ background: '#f0ebfa' }}>
                      <th rowSpan={2} className="small text-muted fw-semibold py-2 px-3" style={{ background: '#f8fafc', verticalAlign: 'middle' }}>#</th>
                      <th rowSpan={2} className="small text-muted fw-semibold py-2 px-3" style={{ background: '#f8fafc', verticalAlign: 'middle' }}>Status</th>
                      <th rowSpan={2} className="small text-muted fw-semibold py-2 px-3" style={{ background: '#f8fafc', verticalAlign: 'middle' }}>User</th>
                      {stageGroups.map((g, gi) => (
                        <th
                          key={g.stageLabel}
                          colSpan={g.columns.length}
                          className="small fw-semibold py-2 px-3 text-center"
                          style={{
                            background: STAGE_TINT,
                            color: '#4e2e80',
                            borderBottom: '2px solid #c4b5fd',
                            borderLeft: gi > 0 ? '2px solid #c4b5fd' : undefined,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <i className="bi bi-layers me-1" style={{ fontSize: 10 }} />
                          {g.stageLabel}
                        </th>
                      ))}
                      <th rowSpan={2} className="small text-muted fw-semibold py-2 px-3" style={{ background: '#f8fafc', verticalAlign: 'middle' }}>Submitted At</th>
                      <th rowSpan={2} style={{ background: '#f8fafc' }} />
                    </tr>
                  )}
                  {/* ── Field header row ── */}
                  <tr style={{ background: '#f8fafc' }}>
                    {stageGroups.length === 0 && (
                      <>
                        <th className="small text-muted fw-semibold py-2 px-3">#</th>
                        <th className="small text-muted fw-semibold py-2 px-3">Status</th>
                        <th className="small text-muted fw-semibold py-2 px-3">User</th>
                      </>
                    )}
                    {orderedColumns.map(col => (
                      <th
                        key={col.id}
                        className="small text-muted fw-semibold py-2 px-3"
                        style={{
                          maxWidth: 160,
                          whiteSpace: 'nowrap',
                          background: stageGroups.length > 0 ? stageTint(col.id) : undefined,
                          borderLeft: stageGroups.length > 0 ? stageBorder(col.id) : undefined,
                        }}
                      >
                        <span
                          title={col.label}
                          style={{
                            display: 'inline-block',
                            maxWidth: col.isArchived || col.isOrphaned ? 90 : 130,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            verticalAlign: 'bottom',
                            cursor: 'default',
                          }}
                        >
                          {col.label}
                        </span>
                        {col.isArchived && (
                          <span className="badge ms-1" style={{ fontSize: 9, background: '#94a3b8', color: '#fff', fontWeight: 500 }}>
                            Archived
                          </span>
                        )}
                        {col.isOrphaned && (
                          <span className="badge ms-1" style={{ fontSize: 9, background: '#f59e0b', color: '#fff', fontWeight: 500 }}>
                            Removed
                          </span>
                        )}
                      </th>
                    ))}
                    {stageGroups.length === 0 && (
                      <>
                        <th className="small text-muted fw-semibold py-2 px-3">Submitted At</th>
                        <th className="small text-muted fw-semibold py-2 px-3" />
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((sub, idx) => (
                    <tr key={sub.id}>
                      <td className="px-3 py-2 align-middle text-muted small">
                        {(page - 1) * 25 + idx + 1}
                      </td>
                      <td className="px-3 py-2 align-middle">
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: 10,
                          fontSize: 10,
                          fontWeight: 600,
                          background: sub.status === 'draft' ? '#fef9c3' : '#dcfce7',
                          color:      sub.status === 'draft' ? '#854d0e' : '#166534',
                        }}>
                          {sub.status === 'draft' ? 'Draft' : 'Submitted'}
                        </span>
                      </td>
                      <td className="px-3 py-2 align-middle small text-muted"
                        style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {sub.submitted_by_email || <span style={{ opacity: 0.4 }}>—</span>}
                      </td>
                      {orderedColumns.map(col => (
                        <td
                          key={col.id}
                          className="px-3 py-2 align-middle small"
                          style={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: (col.isArchived || col.isOrphaned) ? '#94a3b8' : undefined,
                            background: stageGroups.length > 0 ? stageTint(col.id) : undefined,
                            borderLeft: stageGroups.length > 0 ? stageBorder(col.id) : undefined,
                          }}
                        >
                          {String(sub.data?.[col.id] ?? '—')}
                        </td>
                      ))}
                      <td className="px-3 py-2 align-middle small text-muted">
                        {formatDate(sub.submitted_at)}
                      </td>
                      <td className="px-3 py-2 align-middle">
                        <button
                          type="button"
                          className="btn btn-sm"
                          style={{ background: '#fee2e2', color: '#dc2626', border: 'none', padding: '2px 8px' }}
                          onClick={() => handleDelete(sub.id)}
                          disabled={deleting === sub.id}
                        >
                          {deleting === sub.id
                            ? <span className="spinner-border spinner-border-sm" />
                            : <i className="bi bi-trash" />
                          }
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="d-flex align-items-center justify-content-between mt-3">
                <small className="text-muted">
                  Page {page} of {totalPages} — {totalCount} submissions
                </small>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                  >
                    <i className="bi bi-chevron-left" /> Prev
                  </button>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                  >
                    Next <i className="bi bi-chevron-right" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
